import React, { useRef, useState, useEffect, useCallback } from 'react';

const COMPLETION_THRESHOLD = 0.98;

export default function VideoPlayer({ src, onComplete, isCompleted }) {
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const isSeeking = useRef(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [isPlaying, setIsPlaying]     = useState(false);
  const [isMuted, setIsMuted]         = useState(false);
  const [volume, setVolume]           = useState(1);
  const [isDone, setIsDone]           = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);

  useEffect(() => {
    maxWatchedRef.current = 0;
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setIsDone(false);
    setShowSkipWarning(false);
    if (videoRef.current) videoRef.current.load();
  }, [src]);

  useEffect(() => {
    if (isCompleted) setIsDone(true);
  }, [isCompleted]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || isSeeking.current) return;
    const ct  = video.currentTime;
    const dur = video.duration;
    if (ct > maxWatchedRef.current) maxWatchedRef.current = ct;
    setCurrentTime(ct);
    // Complete if: known finite duration and watched enough %, OR duration unknown/infinite and watched 10+ seconds
    const knownDone   = isFinite(dur) && dur > 0 && ct / dur >= COMPLETION_THRESHOLD;
    const fallbackDone = (!isFinite(dur) || dur === 0) && ct >= 10;
    if ((knownDone || fallbackDone) && !isDone) {
      setIsDone(true);
      onComplete && onComplete();
    }
  }, [isDone, onComplete]);

  const handleSeeking = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    isSeeking.current = true;
    if (video.currentTime > maxWatchedRef.current + 1.5) {
      video.currentTime = maxWatchedRef.current;
    }
  }, []);

  const handleSeeked = useCallback(() => { isSeeking.current = false; }, []);
  const handleLoadedMetadata = () => { setDuration(videoRef.current?.duration || 0); };
  const handleEnded = () => {
    setIsPlaying(false);
    if (!isDone) { setIsDone(true); onComplete && onComplete(); }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setIsPlaying(true); }
    else          { v.pause(); setIsPlaying(false); }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) videoRef.current.volume = val;
    setIsMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleProgressClick = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const target = ratio * duration;
    if (target <= maxWatchedRef.current) {
      v.currentTime = target;
      setCurrentTime(target);
    } else {
      setShowSkipWarning(true);
      setTimeout(() => setShowSkipWarning(false), 2500);
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const pctCurrent = duration ? Math.min((currentTime / duration) * 100, 100) : 0;
  const pctWatched = duration ? Math.min((maxWatchedRef.current / duration) * 100, 100) : 0;

  return (
    <div className="video-player-wrap" onContextMenu={(e) => e.preventDefault()}>
      <video
        ref={videoRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onSeeking={handleSeeking}
        onSeeked={handleSeeked}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        playsInline
      />

      {isDone && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(39,174,96,0.92)', color: '#fff', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 700 }}>
            ✓
          </div>
        </div>
      )}

      {showSkipWarning && (
        <div style={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', background: 'rgba(231,76,60,0.92)', color: '#fff', padding: '8px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 10 }}>
          ⛔ Forward skipping is not allowed — please watch the video in full.
        </div>
      )}

      <div className="watch-lock-notice">🔒 No skipping</div>

<div className="video-controls">
        <div className="progress-bar-track" onClick={handleProgressClick} title="Click to rewind. Forward skipping disabled.">
          <div className="progress-bar-watched" style={{ width: `${pctWatched}%` }} />
          <div className="progress-bar-current" style={{ width: `${pctCurrent}%` }} />
        </div>
        <div className="controls-row">
          <button className="ctrl-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <span className="time-display">{fmt(currentTime)} / {fmt(duration)}</span>
          <div className="volume-control">
            <button className="ctrl-btn" onClick={toggleMute} aria-label="Toggle mute">
              {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
            </button>
            <input className="volume-slider" type="range" min="0" max="1" step="0.05" value={isMuted ? 0 : volume} onChange={handleVolumeChange} aria-label="Volume" />
          </div>
          {isDone && (
            <span style={{ color: '#2ecc71', fontWeight: 700, fontSize: 13, marginLeft: 'auto' }}>
              ✓ Complete
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
